import 'whatwg-fetch'
import 'intersection-observer'
import 'ie-array-find-polyfill'
import 'core-js/features/array/from'
import 'core-js/features/array/includes'

import Highway from '@dogstudio/highway'
import { TweenMax } from 'gsap'
import { throttle, debounce } from 'throttle-debounce'
import quicklink from 'quicklink/dist/quicklink.mjs'

// Utils
import Menu from './util/Menu'
import IE11PolyFills from './util/IE11PolyFills'
import DOMObserver from './util/DOMObserver'
import locomotiveScroll from 'locomotive-scroll'
import QuoteForm from './util/QuoteForm'
import Button from './util/Button'

// Routes
import Home from './routes/Home'
import Page from './routes/Page'

// Transitions
// import Fade from './transitions/Fade'
import Panel from './transitions/Panel'
import { Expo } from 'gsap/EasePack'
import Callback from './util/Callback'

export default class App {

  /**
  * Loads the page
  * then calls `this.start()` when DOM is ready
  */
  constructor() {
    App.globalData = {
      smoothScroll: false
    }

    this.iePolyfills = new IE11PolyFills()
    this.resize = this.resize.bind(this)
    this.scroll = this.scroll.bind(this)
    this.update = this.update.bind(this)
    this.observerCallBack = this.observerCallBack.bind(this)

    this.raf = null

    this.breadcrumbArray = []

    this.resizeDebounced = debounce(100, this.resize)
    this.resizeThrottled = throttle(150, this.resize)

    if (!App.globalData.smoothScroll) {
      this.scrollDebounced = debounce(100, this.scroll)
      this.scrollThrottled = throttle(50, this.scroll)
    }

    this.checkMobile()
    this.start()
  }

  /**
  * Inits everything that is app-wide
  * ie: Menu, scroll / resize events...
  *
  * @returns {void}
  */
  start() {
    if (App.globalData.smoothScroll) this.initSmoothScroll()

    this.menu = new Menu()
    this.quoteForm = new QuoteForm()
    if (!App.isMobile) this.buttons = new Button()
    this.w4c = new Callback()

    this.initHighway().then(() => {
      this.events()

      this.menu.addActiveToCurrentView(this.currentRenderer)

      TweenMax.fromTo(document.querySelector('.app'), 0.5, { alpha: 0 }, {
        alpha: 1,
        delay: 1,
        onStart: () => {
          window.scrollTo(0, 0)
          this.raf = requestAnimationFrame(this.update)
        },
        onComplete: () => {
          App.smoothScroll && App.smoothScroll.update()
          document.body.classList.add('page-ready')
          if (!App.globalData.smoothScroll) this.initDOMObserver()
          this.quoteForm.handleEnter()
          this.checkAnchor()
          this.createBreadcrumb()
        }
      })
    })
  }

  initDOMObserver() {
    App.DOMObserver = new DOMObserver(this.observerCallBack)
  }

  initSmoothScroll() {
    /* eslint-disable-next-line */
    App.smoothScroll = new locomotiveScroll({
      el: document.body.querySelector('.js-scroll'),
      smooth: true,
      passive: true,
      inertia: 1.0
    })
  }

  checkMobile() {
    App.isMobile && document.body.classList.remove('isMobile')
    App.isMobile && document.documentElement.classList.remove('isMobile')
    /* eslint-disable */
    App.isMobile = (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
    App.isMobile && document.body.classList.add('isMobile')
    App.isMobile && document.documentElement.classList.add('isMobile')
  }

  /**
  * Starts an Highway instance
  * with our Views and Transitions
  *
  * @returns {void}
  */
  initHighway() {
    return new Promise((resolve) => {
      this.H = new Highway.Core({
          renderers: {
              home: Home,
              page: Page
          },
          transitions: { default: Panel }
      })

      this.setCurrentRenderer()
          .then(resolve)
    })
  }

  setCurrentRenderer() {
    return new Promise((resolve) => {
      this.H.properties.renderer.then(() => {
        this.currentRenderer = this.H.To ? this.H.To : this.H.From

        resolve(this.currentRenderer)
      })
    })
  }

  resize() {
    this.currentRenderer.resize()
    this.menu && this.menu.resize()
  }

  scroll() {
    this.currentRenderer.scroll()
    this.menu && this.menu.scroll()
  }

	update() {
		this.currentRenderer.update()
    this.raf = requestAnimationFrame(this.update)
    this.buttons && !App.isMobile && this.buttons.update()
  }

  observerCallBack(element) {
    if (element.classList.contains('heading', 'js-observer') && !element.classList.contains('hidden')) {
      const chars = element.querySelectorAll('.char > span')

      TweenMax.staggerTo(chars, 0.65, {
        x: '0%',
        ease: Expo.easeOut
      }, 0.02)
    }
  }

  events() {
    window.addEventListener('resize', this.resizeThrottled)
    window.addEventListener('resize', this.resizeDebounced)
    window.addEventListener('orientationchange', this.resize)

    if (App.globalData.smoothScroll) {
      App.smoothScroll && App.smoothScroll.on('scroll', this.scroll)
    } else {
      window.addEventListener('scroll', this.scrollThrottled)
      window.addEventListener('scroll', this.scrollDebounced)
    }

    this.H.on('NAVIGATE_IN', () => {
      this.setCurrentRenderer()
      .then((renderer) => {
          document.title = renderer.properties.page.title
      })
    })

    this.H.on('NAVIGATE_END', ({ from, to, location }) => {
      this.checkAnchor(location)
      this.menu.onPageChange(this.currentRenderer)
      quicklink({ el: to.view })
      if  (!App.globalData.smoothScroll) {
        App.DOMObserver = null
        this.initDOMObserver()
      }
      if (!App.isMobile) {
        this.buttons = null
        this.buttons = new Button()
      }
      this.createBreadcrumb(to)
    })

    window.addEventListener('highwayredirect', (e) => {
      this.H.redirect(e.detail.url)
    })
  }

  createBreadcrumb(to) {
    const breadcrumb = document.querySelector('#breadcrumbs')

    if (to) breadcrumb.innerHTML = to.page.all.breadcrumbs.innerHTML

    TweenMax.to(breadcrumb, 1, {
      delay: 1,
      alpha: 1,
      ease: Expo.easOut
    })

  }

  checkAnchor(location = null) {
    const bodyClassSubmit = Array.from(document.body.classList).find((elt) => elt.includes('formsubmit'))
    let anchor = null

    if (location && location.anchor) anchor = location.anchor
    else if (bodyClassSubmit) {
      const validate = document.querySelector('#gform_confirmation_message_' + bodyClassSubmit.split('-')[1])
      const error = document.querySelector('#gform_wrapper_' + bodyClassSubmit.split('-')[1])

      if (validate) anchor = 'gform_confirmation_message_' + bodyClassSubmit.split('-')[1]
      else if (error) anchor = 'gform_wrapper_' + bodyClassSubmit.split('-')[1]
    } else {
      const idx = window.location.href.indexOf("#")
      if (idx != -1) anchor = window.location.href.substring(idx + 1 )
    }

    if (anchor) {
      const el = document.querySelector('#' + anchor)

      if (el) {
        if (App.smoothScroll) {
          App.smoothScroll.scrollTo(el)
          App.smoothScroll.update()
        } else {
          const elRect = el.getBoundingClientRect()
          window.scrollTo(0, elRect.top)
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./scripts/App.js